import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Image,
  TextInput,
  Platform,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useArticles } from '../contexts/ArticleContext';
import { deleteArticle, updateArticle } from '../services/articlesService';
import { getStores } from '../services/storesService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { selectAndUploadImage } from '../services/uploadService';
import { ScrollView } from 'react-native';

const ArticlesScreen = ({ navigation, route }) => {
  const { articles, loading, loadArticles, removeArticle } = useArticles();

  // Hilfsfunktion: Formatiert quantity (entfernt .00 bei reinen Zahlen, lässt Text unverändert)
  const formatQuantity = (quantity) => {
    if (!quantity && quantity !== 0) return '1';
    
    const quantityStr = String(quantity);
    
    // Prüfe ob es eine reine Zahl ist (nur Ziffern, optional mit . und Nachkommastellen)
    // WICHTIG: /^\d+(\.\d+)?$/ prüft ob der String nur eine Zahl ist, nicht z.B. "1-2" oder "ca. 500"
    if (/^\d+(\.\d+)?$/.test(quantityStr.trim())) {
      const numValue = parseFloat(quantityStr);
      // Wenn es eine ganze Zahl ist (oder .00 am Ende hat), zeige nur die ganze Zahl
      if (numValue % 1 === 0) {
        return String(Math.floor(numValue));
      }
      // Falls es echte Nachkommastellen hat (z.B. 1.5), zeige sie an
      return String(numValue);
    }
    
    // Ansonsten: Text unverändert zurückgeben (z.B. "1-2", "ca. 500g")
    return quantityStr;
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [stores, setStores] = useState([]);
  const [lastUsedStores, setLastUsedStores] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  
  // Neue Features
  const [showFrequencies, setShowFrequencies] = useState(false);
  const [sortByFrequency, setSortByFrequency] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [itemsInShoppingLists, setItemsInShoppingLists] = useState(new Set());
  
  // Für Bearbeiten-Modal
  const [editingArticle, setEditingArticle] = useState(null);
  const [newTagText, setNewTagText] = useState('');
  const [showStoreModal, setShowStoreModal] = useState(false);

  // Läden und Ladenzuordnungen laden
  useEffect(() => {
    const loadStoresAndMappings = async () => {
      try {
        const storesResult = await getStores();
        if (storesResult.success) {
          setStores(storesResult.data);
        }
        
        // Ladenzuordnungen aus AsyncStorage laden
        const storesData = await AsyncStorage.getItem('lastUsedStoresPerArticle');
        if (storesData) {
          setLastUsedStores(JSON.parse(storesData));
        }
      } catch (error) {
        console.error('Error loading stores and mappings:', error);
      }
    };
    
    loadStoresAndMappings();
  }, []);

  // Laden der aktuellen Einkaufslisten und prüfen welche Artikel bereits enthalten sind
  const loadItemsInShoppingLists = async () => {
    try {
      const { getAllShoppingLists } = await import('../services/shoppingService');
      const result = await getAllShoppingLists();
      
      if (result.success) {
        const articleIds = new Set();
        
        result.data.forEach(store => {
          if (store.items) {
            store.items.forEach(item => {
              if (item.article_id) {
                articleIds.add(item.article_id);
              }
            });
          }
        });
        
        setItemsInShoppingLists(articleIds);
      }
    } catch (error) {
      console.error('Error loading shopping list items:', error);
    }
  };

  // Einkaufslisten-Status beim Laden der Artikel aktualisieren
  useEffect(() => {
    if (articles.length > 0) {
      loadItemsInShoppingLists();
    }
  }, [articles]);

  // Bild auswählen und hochladen
  const handleSelectImage = async () => {
    if (!editingArticle) return;
    
    try {
      setEditingArticle(prev => ({ ...prev, uploadingImage: true }));
      
      const result = await selectAndUploadImage();
      
      if (result && result.success && result.imageUrl) {
        setEditingArticle(prev => ({ ...prev, imageUrl: result.imageUrl, uploadingImage: false }));
        if (Platform.OS === 'web') {
          window.alert('Bild wurde erfolgreich hochgeladen');
        } else {
          Alert.alert('Erfolg', 'Bild wurde erfolgreich hochgeladen');
        }
      } else if (result && result.imageUrl) {
        // Fallback für direkte URL
        setEditingArticle(prev => ({ ...prev, imageUrl: result.imageUrl, uploadingImage: false }));
      } else {
        setEditingArticle(prev => ({ ...prev, uploadingImage: false }));
      }
    } catch (error) {
      console.error('Fehler beim Auswählen des Bildes:', error);
      setEditingArticle(prev => ({ ...prev, uploadingImage: false }));
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Hochladen des Bildes');
      } else {
        Alert.alert('Fehler', 'Fehler beim Hochladen des Bildes');
      }
    }
  };

  // Reagiere auf Header-Button
  useEffect(() => {
    if (route.params?.openCreateModal) {
      // Navigiere zur ArticleDetail Screen (wie ursprünglich)
      navigation.navigate('ArticleDetail', {});
      // Parameter zurücksetzen
      navigation.setParams({ openCreateModal: false });
    }
  }, [route.params?.openCreateModal]);

  // Artikel nur beim ersten Mal laden (mit Ref um Endlosschleifen zu vermeiden)
  const hasAttemptedLoad = useRef(false);
  useEffect(() => {
    if (articles.length === 0 && !loading && !hasAttemptedLoad.current) {
      console.log('ArticlesScreen: Loading articles for the first time');
      hasAttemptedLoad.current = true;
      loadArticles().catch(() => {
        // Bei Fehler: Markiere als versucht, aber erlaube erneuten Versuch nach einer Weile
        setTimeout(() => {
          hasAttemptedLoad.current = false;
        }, 5000);
      });
    }
  }, [articles.length, loading]); // loadArticles aus Dependencies entfernt

  // Gefilterte und alphabetisch sortierte Artikel
  const filteredArticles = useMemo(() => {
    let filtered = articles;
    
    // Filter basierend auf Suchanfrage
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = articles.filter(article => {
        // Suche in Name
        if (article.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Suche in Tags
        if (article.default_tags && article.default_tags.some(tag => 
          tag.toLowerCase().includes(query)
        )) {
          return true;
        }
        
        // Suche in Notizen
        if (article.notes && article.notes.toLowerCase().includes(query)) {
          return true;
        }
        
        return false;
      });
    }
    
    // Sortierung
    if (sortByFrequency) {
      return filtered.sort((a, b) => (b.purchase_count || 0) - (a.purchase_count || 0));
    } else {
      return filtered.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    }
  }, [articles, searchQuery, sortByFrequency]);

  const handleBulkDelete = async () => {
    try {
      const selectedIds = Array.from(selectedArticles);
      let successCount = 0;
      
      for (const articleId of selectedIds) {
        const result = await deleteArticle(articleId);
        if (result.success) {
          successCount++;
          removeArticle(articleId);
        }
      }
      
      setSelectedArticles(new Set());
      setBulkMode(false);
      
      if (Platform.OS === 'web') {
        alert(`${successCount} von ${selectedIds.length} Artikeln erfolgreich gelöscht.`);
      } else {
        Alert.alert('Erfolg', `${successCount} von ${selectedIds.length} Artikeln erfolgreich gelöscht.`);
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      if (Platform.OS === 'web') {
        alert('Fehler beim Löschen der Artikel');
      } else {
        Alert.alert('Fehler', 'Fehler beim Löschen der Artikel');
      }
    }
  };

  const handleBulkAddToShoppingList = async () => {
    try {
      const selectedIds = Array.from(selectedArticles);
      const selectedArticlesData = articles.filter(article => selectedIds.includes(article.id));
      
      // Lade Läden falls noch nicht geladen
      let currentStores = stores;
      if (currentStores.length === 0) {
        try {
          const storesResult = await getStores();
          if (storesResult.success) {
            currentStores = storesResult.data;
            setStores(storesResult.data);
            if (storesResult.data.length === 0) {
              if (Platform.OS === 'web') {
                alert('Keine Läden verfügbar. Bitte erstellen Sie zuerst einen Laden.');
              } else {
                Alert.alert('Fehler', 'Keine Läden verfügbar. Bitte erstellen Sie zuerst einen Laden.');
              }
              return;
            }
          } else {
            if (Platform.OS === 'web') {
              alert('Fehler beim Laden der Läden: ' + storesResult.error);
            } else {
              Alert.alert('Fehler', 'Fehler beim Laden der Läden: ' + storesResult.error);
            }
            return;
          }
        } catch (error) {
          console.error('Error loading stores:', error);
          if (Platform.OS === 'web') {
            alert('Fehler beim Laden der Läden');
          } else {
            Alert.alert('Fehler', 'Fehler beim Laden der Läden');
          }
          return;
        }
      }
      
      // Gruppiere Artikel nach ihren zugeordneten Läden
      const articlesByStore = {};
      const defaultStore = currentStores[0]; // Fallback zum ersten Laden
      
      selectedArticlesData.forEach(article => {
        const storeId = article.last_used_store_id || lastUsedStores[article.id];
        const targetStoreId = storeId || defaultStore.id;
        
        if (!targetStoreId) {
          return;
        }
        
        if (!articlesByStore[targetStoreId]) {
          articlesByStore[targetStoreId] = [];
        }
        articlesByStore[targetStoreId].push(article);
      });
      
      // Füge Artikel zu ihren jeweiligen Läden hinzu
      let totalSuccessCount = 0;
      let totalArticlesCount = selectedArticlesData.length;
      
      for (const [storeId, articlesForStore] of Object.entries(articlesByStore)) {
        const successCount = await addArticlesToStore(storeId, articlesForStore);
        totalSuccessCount += successCount;
      }
      
      setSelectedArticles(new Set());
      setBulkMode(false);
      
      if (Platform.OS === 'web') {
        alert(`${totalSuccessCount} von ${totalArticlesCount} Artikeln erfolgreich zur Einkaufsliste hinzugefügt.`);
      } else {
        Alert.alert('Erfolg', `${totalSuccessCount} von ${totalArticlesCount} Artikeln erfolgreich zur Einkaufsliste hinzugefügt.`);
      }
    } catch (error) {
      console.error('Bulk add to shopping list error:', error);
      if (Platform.OS === 'web') {
        alert('Fehler beim Hinzufügen zur Einkaufsliste');
      } else {
        Alert.alert('Fehler', 'Fehler beim Hinzufügen zur Einkaufsliste');
      }
    }
  };

  const addArticlesToStore = async (storeId, articlesToAdd) => {
    try {
      const { addItemToList } = await import('../services/shoppingService');
      let successCount = 0;
      
      for (const article of articlesToAdd) {
        const result = await addItemToList(storeId, {
          article_id: article.id,
          quantity: article.default_quantity || 1,
          unit: article.default_unit || 'Stück',
          notes: article.notes || null,
          tags: article.default_tags || [],
        });
        
        if (result.success) {
          successCount++;
        }
      }
      
      // Einkaufslisten-Status aktualisieren
      await loadItemsInShoppingLists();
      
      return successCount;
    } catch (error) {
      console.error('Add articles to store error:', error);
      throw error;
    }
  };

  const handleStoreSelect = async (article) => {
    const storeId = article.last_used_store_id || lastUsedStores[article.id];
    const selectedStore = storeId ? stores.find(s => s.id === storeId) : null;
    
    const options = [
      {
        text: 'Kein Laden (Standard verwenden)',
        onPress: async () => {
          // Laden-Zuordnung für diesen Artikel entfernen
          const currentStores = { ...lastUsedStores };
          delete currentStores[article.id];
          setLastUsedStores(currentStores);
          await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(currentStores));
        }
      },
      ...stores.map(store => ({
        text: store.name,
        onPress: async () => {
          // Laden-Zuordnung für diesen Artikel speichern
          const updatedStores = {
            ...lastUsedStores,
            [article.id]: store.id
          };
          setLastUsedStores(updatedStores);
          await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(updatedStores));
        }
      }))
    ];

    Alert.alert(
      'Laden wählen',
      `Welchen Laden möchten Sie für "${article.name}" verwenden?`,
      options
    );
  };

  const handleDelete = async (article) => {
    console.log('🗑️ Delete button clicked for:', article.name);
    
    // Für Web: Browser-Confirm verwenden
    if (Platform.OS === 'web') {
      if (window.confirm(`Möchtest du "${article.name}" wirklich löschen?`)) {
        console.log('🔴 Delete confirmed, calling deleteArticle...');
        const result = await deleteArticle(article.id);
        console.log('🔴 Delete result:', result);
        
        if (result.success) {
          // Artikel aus dem Context entfernen
          removeArticle(article.id);
          console.log('✅ Artikel erfolgreich gelöscht');
        } else {
          console.error('❌ Fehler beim Löschen:', result.error);
          alert('Fehler beim Löschen: ' + result.error);
        }
      }
    } else {
      // Für Native: Alert verwenden
      Alert.alert(
        'Artikel löschen',
        `Möchtest du "${article.name}" wirklich löschen?`,
        [
          { text: 'Abbrechen', style: 'cancel' },
          {
            text: 'Löschen',
            style: 'destructive',
            onPress: async () => {
              const result = await deleteArticle(article.id);
              if (result.success) {
                removeArticle(article.id);
              } else {
                Alert.alert('Fehler', result.error);
              }
            },
          },
        ]
      );
    }
  };

  const renderArticle = ({ item }) => {
    // Laden für diesen Artikel bestimmen
    const storeId = item.last_used_store_id || lastUsedStores[item.id];
    const selectedStore = storeId ? stores.find(s => s.id === storeId) : null;
    const isSelected = selectedArticles.has(item.id);
    const isInShoppingList = itemsInShoppingLists.has(item.id);

    const handleArticlePress = () => {
      if (bulkMode) {
        const newSelected = new Set(selectedArticles);
        if (isSelected) {
          newSelected.delete(item.id);
        } else {
          newSelected.add(item.id);
        }
        setSelectedArticles(newSelected);
      } else {
        // Bearbeiten-Modal öffnen
        setEditingArticle({
          id: item.id,
          name: item.name,
          quantity: formatQuantity(item.default_quantity),
          unit: item.default_unit || '',
          tags: item.default_tags || [],
          notes: item.notes || '',
          storeId: item.last_used_store_id || null,
          imageUrl: item.image_url || '',
          uploadingImage: false
        });
      }
    };

    return (
      <View style={[
        styles.articleCard, 
        bulkMode && isSelected && styles.articleCardSelected,
        isInShoppingList && styles.articleCardInShoppingList
      ]}>
        <TouchableOpacity
          style={styles.articleContent}
          onPress={handleArticlePress}
        >
          <View style={styles.articleMainRow}>
            <View style={styles.articleTextContainer}>
              <View style={styles.articleNameContainer}>
                <View style={styles.articleNameRow}>
                  {showFrequencies && (
                    <View style={styles.frequencyBadge}>
                      <Text style={styles.frequencyText}>
                        {item.purchase_count || 0}
                      </Text>
                    </View>
                  )}
                  <Text style={styles.articleName} numberOfLines={2}>
                    {item.name}
                  </Text>
                  {isInShoppingList && (
                    <View style={styles.shoppingListIndicator}>
                      <Ionicons name="cart" size={16} color="#2E7D32" />
                    </View>
                  )}
                  {item.image_url && (
                    <TouchableOpacity
                      style={styles.imageIconButton}
                      onPress={() => {
                        setSelectedImageUrl(item.image_url);
                        setShowImageModal(true);
                      }}
                    >
                      <Ionicons name="image" size={16} color="#666" />
                    </TouchableOpacity>
                  )}
                </View>
                
                {/* Tags unter dem Artikelnamen */}
                {item.default_tags && item.default_tags.length > 0 && (
                  <View style={styles.tagsContainer}>
                    {item.default_tags.slice(0, 2).map((tag, index) => (
                      <View key={index} style={styles.tag}>
                        <Text style={styles.tagText}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
              
              {item.notes && (
                <Text style={styles.articleNotes} numberOfLines={1}>
                  {item.notes}
                </Text>
              )}
            </View>
          </View>
        </TouchableOpacity>
        
        <View style={styles.articleActions}>
          {/* Laden-Feld */}
          <View style={styles.storeField}>
            <Text style={styles.storeFieldText} numberOfLines={1}>
              {selectedStore ? selectedStore.name : 'Kein Laden'}
            </Text>
          </View>
          
          {/* Menge-Feld (Zahl über Einheit) */}
          <View style={styles.quantityField}>
            <View style={styles.quantityContainer}>
              <Text style={styles.quantityText}>{formatQuantity(item.default_quantity)}</Text>
              <Text style={styles.unitText}>{item.default_unit}</Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Suchleiste */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search-outline" size={20} color="#666" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Artikel suchen..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#999"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              style={styles.clearButton}
            >
              <Ionicons name="close-circle" size={20} color="#666" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Toolbar */}
      <View style={styles.toolbar}>
        <TouchableOpacity
          style={[styles.toolbarButton, showFrequencies && styles.toolbarButtonActive]}
          onPress={() => setShowFrequencies(!showFrequencies)}
        >
          <Ionicons name="bar-chart-outline" size={20} color={showFrequencies ? "#fff" : "#666"} />
          <Text style={[styles.toolbarButtonText, showFrequencies && styles.toolbarButtonTextActive]}>
            Häufigkeiten
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.toolbarButton, sortByFrequency && styles.toolbarButtonActive]}
          onPress={() => setSortByFrequency(!sortByFrequency)}
        >
          <Ionicons name={sortByFrequency ? "trending-down-outline" : "sort-outline"} size={20} color={sortByFrequency ? "#fff" : "#666"} />
          <Text style={[styles.toolbarButtonText, sortByFrequency && styles.toolbarButtonTextActive]}>
            {sortByFrequency ? "Häufigkeit" : "A-Z"}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.toolbarButton, bulkMode && styles.toolbarButtonActive]}
          onPress={() => {
            setBulkMode(!bulkMode);
            if (bulkMode) {
              setSelectedArticles(new Set());
            }
          }}
        >
          <Ionicons name="checkmark-circle-outline" size={20} color={bulkMode ? "#fff" : "#666"} />
          <Text style={[styles.toolbarButtonText, bulkMode && styles.toolbarButtonTextActive]}>
            Auswählen
          </Text>
        </TouchableOpacity>
      </View>

      {/* Bulk-Actions */}
      {bulkMode && selectedArticles.size > 0 && (
        <View style={styles.bulkActions}>
          <TouchableOpacity
            style={styles.bulkActionButton}
            onPress={handleBulkAddToShoppingList}
          >
            <Ionicons name="cart-outline" size={20} color="#2E7D32" />
            <Text style={styles.bulkActionText}>Zur Einkaufsliste</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.bulkActionButton, styles.bulkActionButtonDanger]}
            onPress={async () => {
              const selectedCount = selectedArticles.size;
              if (Platform.OS === 'web') {
                if (window.confirm(`Möchten Sie ${selectedCount} Artikel wirklich löschen?`)) {
                  await handleBulkDelete();
                }
              } else {
                Alert.alert(
                  'Artikel löschen',
                  `Möchten Sie ${selectedCount} Artikel wirklich löschen?`,
                  [
                    { text: 'Abbrechen', style: 'cancel' },
                    { text: 'Löschen', style: 'destructive', onPress: handleBulkDelete }
                  ]
                );
              }
            }}
          >
            <Ionicons name="trash-outline" size={20} color="#d32f2f" />
            <View style={styles.bulkActionTextContainer}>
              <Text style={[styles.bulkActionText, styles.bulkActionTextDanger]}>Alle ausgewählten</Text>
              <Text style={[styles.bulkActionText, styles.bulkActionTextDanger]}>Artikel LÖSCHEN</Text>
            </View>
          </TouchableOpacity>
        </View>
      )}

      <FlatList
        data={filteredArticles}
        renderItem={renderArticle}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="list-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>
              {searchQuery ? 'Keine Artikel gefunden' : 'Keine Artikel vorhanden'}
            </Text>
            <Text style={styles.emptySubtext}>
              {searchQuery ? 'Versuche einen anderen Suchbegriff' : 'Erstelle deinen ersten Artikel'}
            </Text>
          </View>
        }
        ListFooterComponent={
          searchQuery && searchQuery.trim() ? (
            <View style={styles.createArticleFooter}>
              <TouchableOpacity
                style={styles.createArticleFooterButton}
                onPress={() => navigation.navigate('ArticleDetail', { articleId: null, searchQuery: searchQuery })}
              >
                <Ionicons name="add-circle-outline" size={20} color="#2E7D32" />
                <Text style={styles.createArticleFooterText}>
                  "{searchQuery}" als neuen Artikel erstellen
                </Text>
              </TouchableOpacity>
            </View>
          ) : null
        }
      />

      {/* Bearbeiten-Modal */}
      <Modal
        visible={editingArticle !== null}
        transparent
        animationType="slide"
        onRequestClose={() => setEditingArticle(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {editingArticle && (
              <ScrollView>
                <Text style={styles.modalTitle}>{editingArticle.name}</Text>

                {/* Laden-Auswahl */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Laden:</Text>
                  <TouchableOpacity
                    style={styles.storeSelectButton}
                    onPress={() => setShowStoreModal(true)}
                  >
                    <Text style={styles.storeSelectText}>
                      {editingArticle.storeId ? stores.find(s => s.id === editingArticle.storeId)?.name || 'Laden wählen' : 'Kein Laden (Standard)'}
                    </Text>
                    <Ionicons name="chevron-down" size={20} color="#666" />
                  </TouchableOpacity>
                </View>

                {/* Menge-Eingabe */}
                <View style={styles.modalSection}>
                  <View style={styles.quantityLabelRow}>
                    <Text style={styles.modalLabel}>Menge:</Text>
                    <TextInput
                      style={styles.unitInput}
                      value={editingArticle.unit || ''}
                      onChangeText={(text) => setEditingArticle({...editingArticle, unit: text})}
                      placeholder="Einheit"
                    />
                  </View>
                  <View style={styles.quantityInputContainer}>
                    <TextInput
                      style={styles.quantityInput}
                      value={editingArticle.quantity?.toString() || ''}
                      onChangeText={(text) => {
                        // Erlaube beliebigen Text für Menge (z.B. "2-3", "ca. 500g", etc.)
                        setEditingArticle({...editingArticle, quantity: text});
                      }}
                      placeholder="z.B. 2, 2-3, ca. 500"
                    />
                    <View style={styles.quickInputButtons}>
                      {[1, 2, 3, 4, 5, 6].map(number => (
                        <TouchableOpacity
                          key={number}
                          style={styles.quickInputButton}
                          onPress={() => setEditingArticle({...editingArticle, quantity: number.toString()})}
                        >
                          <Text style={styles.quickInputButtonText}>{number}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                </View>

                {/* Bild */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Bild:</Text>
                  <TouchableOpacity
                    style={styles.imageSelectButton}
                    onPress={handleSelectImage}
                    disabled={editingArticle.uploadingImage}
                  >
                    {editingArticle.uploadingImage ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <ActivityIndicator size="small" color="#2E7D32" style={{ marginRight: 8 }} />
                        <Text style={styles.imageSelectText}>Lädt...</Text>
                      </View>
                    ) : editingArticle.imageUrl ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={styles.imageSelectText}>Bild ausgewählt</Text>
                        <Image 
                          source={{ uri: editingArticle.imageUrl }} 
                          style={{ width: 30, height: 30, borderRadius: 4, marginLeft: 8 }}
                        />
                      </View>
                    ) : (
                      <Text style={styles.imageSelectText}>Bild auswählen</Text>
                    )}
                    {!editingArticle.uploadingImage && (
                      <Ionicons name="image-outline" size={20} color="#666" />
                    )}
                  </TouchableOpacity>
                </View>

                {/* Notizen */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Notizen:</Text>
                  <TextInput
                    style={styles.notesInput}
                    value={editingArticle.notes || ''}
                    onChangeText={(text) => setEditingArticle({...editingArticle, notes: text})}
                    placeholder="Notizen zum Artikel..."
                    multiline
                    numberOfLines={3}
                  />
                </View>

                {/* Tags-Eingabe */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Tags:</Text>
                  <View style={styles.tagsInputContainer}>
                    {editingArticle.tags?.map((tag, index) => (
                      <View key={index} style={styles.tagInputItem}>
                        <Text style={styles.tagInputText}>{tag}</Text>
                        <TouchableOpacity
                          style={styles.tagRemoveButton}
                          onPress={() => {
                            const newTags = editingArticle.tags?.filter((_, i) => i !== index) || [];
                            setEditingArticle({...editingArticle, tags: newTags});
                          }}
                        >
                          <Ionicons name="close" size={16} color="#666" />
                        </TouchableOpacity>
                      </View>
                    ))}
                    <TextInput
                      style={styles.tagInput}
                      value={newTagText}
                      onChangeText={setNewTagText}
                      placeholder="Tag hinzufügen..."
                      onSubmitEditing={() => {
                        if (newTagText.trim() && !editingArticle.tags?.includes(newTagText.trim())) {
                          setEditingArticle({
                            ...editingArticle,
                            tags: [...(editingArticle.tags || []), newTagText.trim()]
                          });
                          setNewTagText('');
                        }
                      }}
                    />
                  </View>
                </View>

                {/* Buttons */}
                <View style={styles.modalButtons}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.cancelButton]}
                    onPress={() => {
                      setEditingArticle(null);
                      setNewTagText('');
                    }}
                  >
                    <Text style={styles.cancelButtonText}>Abbrechen</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.saveButton]}
                    onPress={async () => {
                      try {
                        // Tags verarbeiten: Bestehende Tags + eventuell noch nicht gespeicherter Text
                        let finalTags = [...(editingArticle.tags || [])];
                        if (newTagText.trim()) {
                          finalTags.push(newTagText.trim());
                          setNewTagText('');
                        }

                        // Bestehenden Artikel aktualisieren
                        // WICHTIG: updateArticle aus dem Context aktualisiert den Artikel bereits optimistisch
                        // KEIN loadArticles() mehr aufrufen, damit der Fokus nicht verschoben wird!
                        await updateArticle(editingArticle.id, {
                          name: editingArticle.name || '',
                          default_quantity: editingArticle.quantity || 1,
                          default_unit: editingArticle.unit || 'Stück',
                          default_tags: finalTags,
                          notes: editingArticle.notes || '',
                          image_url: editingArticle.imageUrl || '',
                          last_used_store_id: editingArticle.storeId || null
                        });
                        
                        // Modal schließen (Artikel ist bereits im Context aktualisiert)
                        setEditingArticle(null);
                        setNewTagText('');
                      } catch (error) {
                        console.error('Fehler beim Speichern des Artikels:', error);
                        if (Platform.OS === 'web') {
                          window.alert('Fehler beim Speichern des Artikels');
                        } else {
                          Alert.alert('Fehler', 'Fehler beim Speichern des Artikels');
                        }
                      }
                    }}
                  >
                    <Text style={styles.saveButtonText}>Speichern</Text>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Store-Auswahl Modal */}
      <Modal
        visible={showStoreModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStoreModal(false)}
      >
        <View style={styles.storeModalOverlay}>
          <View style={styles.storeModalContent}>
            <Text style={styles.storeModalTitle}>Laden auswählen</Text>
            
            <ScrollView style={styles.storeList}>
              <TouchableOpacity
                style={[
                  styles.storeOption,
                  !editingArticle?.storeId && styles.storeOptionSelected
                ]}
                onPress={() => {
                  setEditingArticle(prev => ({...prev, storeId: null}));
                  setShowStoreModal(false);
                }}
              >
                <Text style={styles.storeOptionText}>Kein Laden (Standard)</Text>
                {!editingArticle?.storeId && (
                  <Ionicons name="checkmark" size={20} color="#2E7D32" />
                )}
              </TouchableOpacity>
              
              {stores.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[
                    styles.storeOption,
                    editingArticle?.storeId === store.id && styles.storeOptionSelected
                  ]}
                  onPress={() => {
                    setEditingArticle(prev => ({...prev, storeId: store.id}));
                    setShowStoreModal(false);
                  }}
                >
                  <Text style={styles.storeOptionText}>{store.name}</Text>
                  {editingArticle?.storeId === store.id && (
                    <Ionicons name="checkmark" size={20} color="#2E7D32" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Bild-Modal */}
      <Modal
        visible={showImageModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowImageModal(false)}
      >
        <View style={styles.imageModalOverlay}>
          <View style={styles.imageModalContent}>
            <TouchableOpacity
              style={styles.imageModalCloseButton}
              onPress={() => setShowImageModal(false)}
            >
              <Ionicons name="close" size={24} color="#fff" />
            </TouchableOpacity>
            <Image
              source={{ uri: selectedImageUrl }}
              style={styles.imageModalImage}
              resizeMode="contain"
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 0,
    outline: 'none',
    borderWidth: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 2,
  },
  listContent: {
    padding: 10,
  },
  articleCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  articleContent: {
    flex: 1,
  },
  articleMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  articleImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
    marginRight: 12,
  },
  articleImagePlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  articleTextContainer: {
    flex: 1,
  },
  articleNameContainer: {
    flex: 1,
    marginRight: 8,
  },
  articleNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  articleName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  imageIconButton: {
    padding: 4,
    marginLeft: 8,
  },
  imageModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalContent: {
    width: '90%',
    height: '90%',
    position: 'relative',
  },
  imageModalCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalImage: {
    width: '100%',
    height: '100%',
  },
  articleDetails: {
    fontSize: 12,
    color: '#666',
  },
  articleSecondaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  articleNotes: {
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
    flex: 1,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginLeft: 6,
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    marginRight: 4,
  },
  tagText: {
    fontSize: 10,
    color: '#2E7D32',
    fontWeight: '600',
  },
  storeField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    height: 32,
  },
  storeFieldText: {
    fontSize: 10,
    color: '#666',
  },
  quantityField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    height: 32,
  },
  quantityContainer: {
    alignItems: 'center',
    marginRight: 2,
  },
  quantityText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    lineHeight: 14,
  },
  unitText: {
    fontSize: 8,
    color: '#666',
    lineHeight: 10,
  },
  articleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  deleteButton: {
    padding: 6,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 60,
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
  },
  createArticleFooter: {
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  createArticleFooterButton: {
    backgroundColor: '#fff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#2E7D32',
  },
  createArticleFooterText: {
    color: '#2E7D32',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  // Toolbar Styles
  toolbar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    gap: 8,
  },
  toolbarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 4,
  },
  toolbarButtonActive: {
    backgroundColor: '#2E7D32',
  },
  toolbarButtonText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  toolbarButtonTextActive: {
    color: '#fff',
  },
  // Bulk Actions
  bulkActions: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    gap: 8,
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8f0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 4,
  },
  bulkActionButtonDanger: {
    backgroundColor: '#ffebee',
  },
  bulkActionText: {
    fontSize: 12,
    color: '#2E7D32',
    fontWeight: '500',
  },
  bulkActionTextDanger: {
    color: '#d32f2f',
  },
  bulkActionTextContainer: {
    alignItems: 'center',
  },
  // Article Selection
  articleCardSelected: {
    backgroundColor: '#e8f5e9',
    borderColor: '#2E7D32',
    borderWidth: 2,
  },
  articleCardInShoppingList: {
    backgroundColor: '#f0f8f0',
    borderLeftColor: '#2E7D32',
    borderLeftWidth: 4,
  },
  // Frequency Display
  frequencyBadge: {
    backgroundColor: '#2E7D32',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginRight: 8,
    minWidth: 24,
    alignItems: 'center',
  },
  frequencyText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  // Shopping List Indicator
  shoppingListIndicator: {
    backgroundColor: '#e8f5e9',
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  modalSection: {
    marginBottom: 20,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  quantityInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    width: 80,
    marginRight: 12,
    textAlign: 'center',
  },
  unitInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    flex: 1,
  },
  quantityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  quantityInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  quickInputButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  quickInputButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  quickInputButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  imageSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  imageSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  tagsInputContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 50,
  },
  tagInputItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 6,
    marginBottom: 4,
  },
  tagInputText: {
    fontSize: 12,
    color: '#555',
    marginRight: 4,
  },
  tagRemoveButton: {
    padding: 2,
  },
  tagInput: {
    fontSize: 14,
    color: '#333',
    flex: 1,
    minWidth: 100,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  modalButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 5,
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  storeSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  storeSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  storeModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  storeModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '80%',
    maxHeight: '60%',
    maxWidth: 400,
  },
  storeModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    padding: 20,
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  storeList: {
    maxHeight: 300,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  storeOptionSelected: {
    backgroundColor: '#f0f8f0',
  },
  storeOptionText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
});

export default ArticlesScreen;